import React, { Fragment, useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { AlertUnsavedData, NotificationInline, NotificationToaster } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { DefaultEventTypeDetailsModel } from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { FormTextArea, FormTextInput } from "components/form-fields";
import { AssetService } from "services";
import { Intent } from "@blueprintjs/core";
import { AssetSelection } from "components/Asset/AssetSelection";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";

export function EventTypeDetails({
    model = DefaultEventTypeDetailsModel,
    forceLoad = false,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeDetailsModel; },
    onSave = () => { },
    onForceLoadFinished = () => { },
    hasChecksAttached = false
}) {
    var navigate = useNavigate();

    const step = 0;
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);

    const [loading, setLoading] = useState(true);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
    const [optionsFetched, setOptionsFetched] = useState(false);

    const [originalDetailsModel, setOriginalDetailsModel] = useState(DefaultEventTypeDetailsModel);
    const [detailsModel, setDetailsModel] = useState(DefaultEventTypeDetailsModel)
    const [isValid, detailsModelErrors, validateDetailsModel] = useValidation();
    const isDetailsModelDirty = useDirtyData(originalDetailsModel, detailsModel);
    const [assetOptions, setAssetOptions] = useState({});
    const [assetConditionsChanged, setAssetConditionsChanged] = useState(false);

    useEffect(fetchOptions, []);

    useEffect(load, [model, forceLoad]);

    function load() {
        if (!userHasFmsManageAction || (forceLoad === false && initialLoadCompleted)) {
            return;
        }

        setModel(prev => { return {...prev, ...model}; });

        if (
            !isInWizardMode && 
            (
                model.name !== DefaultEventTypeDetailsModel.name ||
                model.eventHelperText !== DefaultEventTypeDetailsModel.eventHelperText ||
                model.additionalProperties?.conditions?.assetTypes !== DefaultEventTypeDetailsModel.additionalProperties?.conditions?.assetTypes ||
                model.additionalProperties?.conditions?.assetSubTypes !== DefaultEventTypeDetailsModel.additionalProperties?.conditions?.assetSubTypes ||
                model.additionalProperties?.conditions?.assetStatuses !== DefaultEventTypeDetailsModel.additionalProperties?.conditions?.assetStatuses ||
                model.additionalProperties?.conditions?.assetOwnerships !== DefaultEventTypeDetailsModel.additionalProperties?.conditions?.assetOwnerships
            )
        ) {
            setOriginalDetailsModel(model);
        }

        setInitialLoadCompleted(true);

        if (forceLoad) {
            onForceLoadFinished();
        }
    }

    function fetchOptions() {
        if (optionsFetched) {
            return;
        }

        setLoading(true);

        AssetService.getAssetOptions()
            .then((assetOptionsResponse) => {
                setAssetOptions(assetOptionsResponse);
            })
            .catch(() => {
                NotificationToaster.show(Intent.WARNING, 'Asset options could not be loaded. Please try again later.');
            })
            .finally(() => {
                setLoading(false);
                setOptionsFetched(true);
            });
    }

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }

        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(() => {
        validate();
        onModelUpdate(step, detailsModel);
    }, [detailsModel])

    useEffect(() => {
        if (assetConditionsChanged && !isDetailsModelDirty) {
            setAssetConditionsChanged(false);
        }
    }, [isDetailsModelDirty])

    function validate() {
        let validationRules = [
            { fieldName: "name", required: true, minLength: 1, maxLength: 100 },
            { fieldName: "eventHelperText", maxLength: 4000 }
        ];

        validateDetailsModel(validationRules, detailsModel);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, value) {
        if (loading) {
            return;
        }

        const clonedModel = {
            ...detailsModel,
            [fieldName]: value
        };

        setDetailsModel(clonedModel);
    }

    function onConditionChange(newCondition) {
        setAssetConditionsChanged(true);
        const clonedModel = {
            ...detailsModel,
            additionalProperties: {
                conditions: newCondition
            }
        };

        setDetailsModel(clonedModel);
    }

    function setModel(changeModel) {
        setOriginalDetailsModel(changeModel);
        setDetailsModel(changeModel);
    }

    function onConfirmLeave(redirectToPath) {
        setModel(originalDetailsModel);
        onModelUpdate(step, originalDetailsModel);
        
        setTimeout(() => navigate(redirectToPath));
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <Fragment>
                                <h2>Event Type Details</h2>
                                <AlertUnsavedData isDirty={isDetailsModelDirty} onConfirmLeave={onConfirmLeave} />
                            </Fragment>
                        )}
                    />

                    <FormTextInput
                        id="input-event-type-name"
                        disabled={saving || loading}
                        loading={loading}
                        value={detailsModel.name}
                        headingText="Event type name"
                        onChange={(event) => onValueChange("name", event.target.value)}
                        dangerHelperText={detailsModelErrors["name"]}
                        fill={true}
                    />

                    <FormTextArea
                        maxLength={4000}
                        disabled={saving}
                        loading={loading}
                        onChange={(item) => onValueChange("eventHelperText", item.currentTarget.value)}
                        headingText="Event type helper text"
                        value={detailsModel.eventHelperText}
                    />

                    <h3>Asset Settings</h3>

                    <NotificationInline 
                        allowClose={false} 
                        text={"Asset settings are disabled as this event type has checks attached. To change asset settings please remove all checks from this event type."} 
                        intent="warning" 
                        show={hasChecksAttached} 
                    />

                    <AssetSelection
                        loading={loading}
                        saving={saving}
                        isReadOnly={hasChecksAttached}
                        onChange={onConditionChange}
                        conditions={detailsModel?.additionalProperties?.conditions}
                        assetOptions={assetOptions}
                        isNew={isInWizardMode}
                    />

                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <>
                                <NotificationInline
                                    allowClose={false}
                                    text={detailsModel.adHoc ? "Future creation of these Event Types will be effected by the asset condition change."
                                        : "Any Asset schedules that no longer fall under this Event Type due to the change in asset conditions will be disabled and related Events that have not been completed will be deleted. Please be aware this could take a short while to process."}
                                    intent="warning"
                                    show={assetConditionsChanged}
                                />

                                <ButtonSave
                                    loading={loading}
                                    onClick={() => onSave("details")}
                                    disabled={saving}
                                    simpleDisabled={!isDetailsModelDirty || !isValid}
                                />
                            </>
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeDetails.propTypes = {
    model: PropTypes.object,
    forceLoad: PropTypes.bool,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func,
    hasChecksAttached: PropTypes.bool
};